import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Ticket } from '@shared/entities/ticket';
import { TicketFilterURL, TicketService } from '@shared/services/api/ticket.service';
import { Observable, of, switchMap, tap } from 'rxjs';
import { ThemeService } from '../services/theme.service';
import { FilterSelectedEventType } from '@shared/models/types';
import { TicketFiltersService } from '@shared/services/api/ticket-filters.service';
import { TicketFilter } from '@shared/entities/ticketFilter';

@Component({
  selector: 'app-ticket',
  templateUrl: './ticket.component.html',
  styleUrl: './ticket.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TicketComponent implements OnInit {
  closedTickets$!: Observable<Ticket[]>;
  closedPrevPage?: number;
  closedCurrentPage?: number;
  closedNextPage?: number;
  closedTotalPages?: number;
  openedPrevPage?: number;
  openedCurrentPage?: number;
  openedNextPage?: number;
  openedTotalPages?: number;
  openedTickets$!: Observable<Ticket[]>;
  ticketFilter$!: Observable<TicketFilter>;
  lastTicket?: Ticket;
  isLight = true;

  constructor(
    public translateService: TranslateService,
    private _themeService: ThemeService,
    private _ticketService: TicketService,
    private _ticketFilterService: TicketFiltersService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // allClosed$/allOpened$ are shareReplay'd on the shared TicketService singleton, so without
    // this the page would show whatever was last cached (e.g. from an earlier visit) instead of
    // a guaranteed-fresh list — unlike the requests list, which has no such cache and always
    // refetches on mount.
    this._ticketService.refreshClosed(1);
    this._ticketService.refreshOpened(1);

    this.closedTickets$ = this._ticketService.allClosed$.pipe(
      switchMap((pagination) => {
        this.closedPrevPage = pagination.prevPage;
        this.closedCurrentPage = pagination.currentPage;
        this.closedNextPage = pagination.nextPage;
        this.closedTotalPages = pagination.totalPage;
        if (pagination.data.length > 0) this.lastTicket = pagination.data[0];
        this._cdr.markForCheck();
        return of(pagination.data);
      })
    );

    this.openedTickets$ = this._ticketService.allOpened$.pipe(
      switchMap((pagination) => {
        this.openedPrevPage = pagination.prevPage;
        this.openedCurrentPage = pagination.currentPage;
        this.openedNextPage = pagination.nextPage;
        this.openedTotalPages = pagination.totalPage;
        this._cdr.markForCheck();
        return of(pagination.data);
      })
    );

    this.ticketFilter$ = this._ticketFilterService.all$;

    this._themeService.refresh$
      .pipe(switchMap(() => this._themeService.isDarkTheme$.pipe(tap((isDarkTheme) => {
        this.isLight = !isDarkTheme;
        this._cdr.markForCheck();
      }))))
      .subscribe();
  }

  onSelectedFilter(filters: FilterSelectedEventType[]) {
    // this._ticket.filtersClosed = filters;
    const filtersName = filters.reduce((acc: any, filter: FilterSelectedEventType) => {
      acc[filter.name] = filter.selectedValue;
      return acc;
    }, {});
    this._ticketFilterService.filters = filtersName as TicketFilterURL;
    this._ticketService.filtersClosed = filtersName as TicketFilterURL;
  }

  onOpenedTicketPage(page: number) {
    this._ticketService.refreshOpened(page);
  }

  onClosedTicketPage(page: number) {
    this._ticketService.refreshClosed(page).pipe(
      tap((pagination) => {
        this.closedPrevPage = pagination.prevPage;
        this.closedCurrentPage = pagination.currentPage;
        this.closedNextPage = pagination.nextPage;
      })
    );
  }
}
